import clsx from 'clsx'
import type {
  AriaAttributes,
  ChangeEvent,
  FocusEvent,
  FocusEventHandler,
  ForwardedRef,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  Ref,
} from 'react'
import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDebouncedCallback } from 'use-debounce'
import { useId } from '../hooks/useId'
import { useSyncRefs } from '../hooks/useSyncRefs'
import IconButton from '../IconButton/IconButton'
import { Close } from '../icons'
import { isFocusWithinSupported } from '../utilities/isFocusWithinSupported'
import { toDataSet } from '../utilities/toDataSet'
import type {
  TextInputSize,
  TextInputStatus,
  TextInputVariant,
  TextInputColor,
} from './styles'
import {
  adornments,
  baseClasses,
  labels,
  sizes,
  variants,
  multilineSizes,
  inputVariants,
  focusedStyles,
  parentSizes,
  appearFocusedStyles,
  labelSpacing,
  labelColors,
} from './styles'

type EnterKeyHint =
  | 'enter'
  | 'done'
  | 'go'
  | 'next'
  | 'previous'
  | 'search'
  | 'send'

type InputMode =
  | 'none'
  | 'text'
  | 'tel'
  | 'url'
  | 'email'
  | 'numeric'
  | 'decimal'
  | 'search'

type SvgIconComponent = {
  uiName: 'SvgIcon'
  (props: { color: string }): JSX.Element
}

export interface TextInputProps {
  appearFocused?: boolean
  autoFocus?: boolean
  color?: TextInputColor
  containerRef?: Ref<HTMLLabelElement>
  dataSet?: Record<string, string | undefined>
  defaultValue?: string
  disabled?: boolean
  endAdornment?: SvgIconComponent | string | ReactNode
  enterKeyHint?: EnterKeyHint
  error?: boolean
  helperText?: string
  inputMode?: InputMode
  inputRef?: Ref<HTMLInputElement | HTMLTextAreaElement>
  label: string
  lines?: number
  max?: string | number
  maxLines?: number
  min?: string | number
  multiline?: boolean
  name?: string
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onClear?: MouseEventHandler<HTMLButtonElement>
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void
  onClick?: MouseEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>
  readOnly?: boolean
  required?: boolean
  selectable?: boolean
  size?: TextInputSize
  startAdornment?: SvgIconComponent | string | ReactNode
  tabIndex?: number
  type?: TextInputType
  variant?: TextInputVariant
}

type ExtraTextInputProps = TextInputProps & {
  'aria-activedescendant'?: AriaAttributes['aria-activedescendant']
  'aria-autocomplete'?: AriaAttributes['aria-autocomplete']
  'aria-controls'?: AriaAttributes['aria-controls']
  'aria-expanded'?: AriaAttributes['aria-expanded']
  'aria-haspopup'?: AriaAttributes['aria-haspopup']
  'aria-labelledby'?: AriaAttributes['aria-labelledby']
  id?: string
  role?: string
}

type TextInputType =
  | 'email'
  | 'number'
  | 'password'
  | 'search'
  | 'tel'
  | 'text'
  | 'url'
  | 'color'

const modes: { [InputType in TextInputType]: InputMode } = {
  email: 'email',
  number: 'numeric',
  password: 'text',
  search: 'search',
  tel: 'tel',
  text: 'text',
  url: 'url',
  color: 'none',
}

const TextInput = forwardRef<HTMLDivElement, TextInputProps>(
  (
    {
      appearFocused = false,
      autoFocus = false,
      color = 'default',
      containerRef,
      dataSet,
      defaultValue = '',
      disabled = false,
      endAdornment: EndAdornment,
      enterKeyHint,
      error = false,
      helperText,
      inputMode,
      inputRef,
      label,
      lines = 0,
      max,
      maxLines = 0,
      min,
      multiline = false,
      name,
      onBlur,
      onChange,
      onClick,
      onClear,
      onFocus,
      onKeyDown,
      readOnly = false,
      required = false,
      selectable = false,
      size = 'medium',
      startAdornment: StartAdornment,
      tabIndex,
      type = 'text',
      variant = 'outlined',
      ...rest
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const id = `coconut-text-input-${useId()}`
    const intl = useIntl()

    const labelRef = useRef<HTMLSpanElement>(null)
    const [hasValue, setHasValue] = useState(!!defaultValue)
    const [shrinkLabel, setShrinkLabel] = useState(!!StartAdornment)

    const localRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null)
    const syncRef = useSyncRefs(localRef, inputRef)

    const handleClear: MouseEventHandler<HTMLButtonElement> = (event) => {
      event?.preventDefault()
      setHasValue(false)

      if (localRef.current) localRef.current.value = ''

      const input = localRef.current
      if (input) {
        const descriptor = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        ) as PropertyDescriptor
        const inputEvent = new Event('input', { bubbles: true })

        descriptor.set?.call(input, '')
        input.dispatchEvent(inputEvent)
      }

      onClear?.(event)
    }

    const debounced = useDebouncedCallback(
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange?.(event)
      },
      300,
    )

    const extraProps = rest as ExtraTextInputProps // to avoid some ts errors without exposing these props
    const status: TextInputStatus = disabled
      ? 'disabled'
      : error
      ? 'error'
      : 'default'

    const rows = lines ? lines : size === 'medium' ? 4 : 3

    const handleBlur = (
      event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      // handle focused styles manually in browsers that do not support ':focus-within'
      if (!isFocusWithinSupported() && !extraProps?.['aria-expanded']) {
        appearFocusedStyles[variant][status][color]
          .split(' ')
          .forEach((className) => {
            event.target.parentElement?.classList.remove(className)
          })
      }

      onBlur?.(event)
    }

    const autosizeTextarea = (event: ChangeEvent<HTMLTextAreaElement>) => {
      const target = event.target

      target.rows = rows // so that we can auto shrink
      const lineHeight = parseInt(window.getComputedStyle(target).lineHeight)

      const newRows = Math.ceil(target.scrollHeight / lineHeight)

      if (newRows > rows) {
        if (maxLines <= 0 || newRows <= maxLines) {
          target.rows = newRows
        } else {
          target.rows = maxLines
        }
      }
    }

    const handleChange = (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      event.persist()

      setHasValue(!!localRef.current?.value)

      if (selectable) {
        onChange?.(event)
      } else {
        debounced.callback(event)
      }

      if (multiline) {
        autosizeTextarea(event as ChangeEvent<HTMLTextAreaElement>)
      }
    }

    const handleFocus: FocusEventHandler<
      HTMLInputElement | HTMLTextAreaElement
    > = (event) => {
      // handle focused styles manually in browsers that do not support ':focus-within'
      if (!isFocusWithinSupported()) {
        appearFocusedStyles[variant][status][color]
          .split(' ')
          .forEach((className) => {
            event.target.parentElement?.classList.add(className)
          })
      }

      onFocus?.(event)
    }

    // If end adornment is long enough to overlap the label, shrink the label to the top
    useLayoutEffect(() => {
      if (
        !shrinkLabel &&
        labelRef.current &&
        localRef.current &&
        labelRef.current?.clientWidth + labelRef.current?.offsetLeft >
          localRef.current?.clientWidth + localRef.current?.offsetLeft
      ) {
        setShrinkLabel(true)
      }
    }, [EndAdornment, shrinkLabel])

    const commonProps = {
      'aria-activedescendant': extraProps?.['aria-activedescendant'],
      'aria-autocomplete': extraProps?.['aria-autocomplete'],
      'aria-controls': extraProps?.['aria-controls'],
      'aria-describedby': helperText ? `helper-text-${id}` : undefined,
      'aria-expanded': extraProps?.['aria-expanded'],
      'aria-haspopup': extraProps?.['aria-haspopup'],
      'aria-labelledby':
        extraProps?.['aria-labelledby'] || `label-${extraProps?.id || id}`,
      autoFocus: autoFocus,
      className: clsx(
        baseClasses.input,
        inputVariants[status][color],
        multiline
          ? [
              multilineSizes[variant][size],
              'overflow-x-hidden break-words no-scrollbar resize-none',
            ]
          : [sizes[variant][size], 'truncate'],
        selectable ? 'cursor-pointer' : '',
      ),
      disabled: disabled,
      id: extraProps?.id,
      inputMode: inputMode || modes[type],
      name: name,
      onBlur: handleBlur,
      onChange: handleChange,
      onClick: onClick,
      onFocus: handleFocus,
      onKeyDown: onKeyDown,
      placeholder: ' ',
      readOnly,
      required,
      role: extraProps?.role,
      tabIndex: tabIndex,
      type: type,
      defaultValue: defaultValue,
    }

    return (
      <div className={baseClasses.container}>
        <label className="relative" ref={containerRef}>
          <div
            className={clsx(
              baseClasses.inputWrapper,
              focusedStyles[variant][status][color],
              appearFocused
                ? appearFocusedStyles[variant][status][color]
                : variants[variant][status][color],
              multiline ? '' : parentSizes[variant][size],
            )}
            ref={ref}
          >
            {StartAdornment ? (
              <span
                className={clsx(
                  baseClasses.start,
                  variant === 'filled' ? 'pt-3' : null,
                  multiline
                    ? adornments.multilineSize[variant][size]
                    : adornments.size[variant][size],
                  adornments.text[status][color],
                  typeof StartAdornment === 'function' &&
                    'uiName' in StartAdornment &&
                    StartAdornment.uiName === 'SvgIcon'
                    ? adornments.icon
                    : null,
                )}
              >
                {typeof StartAdornment === 'function' &&
                'uiName' in StartAdornment &&
                StartAdornment.uiName === 'SvgIcon' ? (
                  <StartAdornment color="inherit" />
                ) : (
                  StartAdornment
                )}
              </span>
            ) : null}
            {multiline ? (
              <textarea {...commonProps} ref={syncRef} rows={rows} />
            ) : (
              <input
                {...commonProps}
                {...toDataSet(dataSet)}
                enterKeyHint={enterKeyHint}
                max={max}
                min={min}
                ref={syncRef}
              />
            )}
            {onClear && hasValue ? (
              <span
                className={clsx(
                  baseClasses.end,
                  multiline
                    ? adornments.multilineSize[variant][size]
                    : adornments.size[variant][size],
                  adornments.text[status][color],
                  EndAdornment ? 'mr-0.5' : 'mr-2.5',
                )}
              >
                <IconButton
                  aria-label={intl.formatMessage(
                    { id: 'TextInput.clear_labelled_input' },
                    { label },
                  )}
                  color={color}
                  onClick={handleClear}
                  size="small"
                >
                  <Close />
                </IconButton>
              </span>
            ) : null}
            {EndAdornment ? (
              <span
                className={clsx(
                  baseClasses.end,
                  multiline
                    ? adornments.multilineSize[variant][size]
                    : adornments.size[variant][size],
                  adornments.text[status][color],
                  typeof EndAdornment === 'function' &&
                    'uiName' in EndAdornment &&
                    EndAdornment.uiName === 'SvgIcon'
                    ? adornments.icon
                    : null,
                  'mr-3',
                )}
              >
                {typeof EndAdornment === 'function' &&
                'uiName' in EndAdornment &&
                EndAdornment.uiName === 'SvgIcon' ? (
                  <EndAdornment color="inherit" />
                ) : (
                  EndAdornment
                )}
              </span>
            ) : null}
            <span
              className={clsx(
                'input-label',
                variant === 'outlined'
                  ? 'input-label-outlined'
                  : 'input-label-filled',
                size === 'medium' ? 'input-label-medium' : 'input-label-small',
                baseClasses.label,
                disabled ? 'cursor-not-allowed' : 'cursor-text',
                appearFocused
                  ? `${labels.focused[variant]} ${labelColors.focused[variant][status][color]}`
                  : shrinkLabel
                  ? `${labels.value[variant]} ${labelColors.value[variant][status][color]}`
                  : `${labels.inactive[variant]} ${labelColors.inactive[variant][status][color]}`,
                appearFocused || shrinkLabel
                  ? labelSpacing.focused[variant][size]
                  : labelSpacing.inactive[variant][size],
              )}
              id={`label-${extraProps?.id || id}`}
              ref={labelRef}
            >
              {label}
            </span>
          </div>
        </label>
        <div
          aria-live={error ? 'assertive' : 'off'}
          className={
            helperText
              ? clsx(
                  baseClasses.help,
                  labelColors.value[variant][status][color],
                )
              : 'sr-only'
          }
          id={`helper-text-${id}`}
        >
          {helperText ? helperText : null}
        </div>
      </div>
    )
  },
)
export default TextInput
